import React, { Suspense, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import AppLoader from 'core/ui/components/AppLoader';
import * as RestApi from 'core/network/RestApi';
import { Layout, TabBarAndWindowLayout, SplitPanelWrapper, SplitPanelContainer, Overlay } from './components/style';
import { TemplateEditorBottomToolbar } from './components/TemplateEditorBottomToolbar';
import { TEMPLATE_EDITOR_SPLIT_CONTAINER_CSS_ID } from './constants';
import { TemplateEditorSideTabBar } from './components/TemplateEditorSideTabBar';
import { TemplateEditorNameBar } from './components/TemplateEditorNameBar';
import { TemplateEditorLeftWindow } from './components/TemplateEditorLeftWindow';
import { TemplateEditorRightWindow } from './components/TemplateEditorRightWindow';
import { useTemplateEditorDispatch, useTemplateEditorState } from './context/UseTemplateEditorContext';
import { ITemplate } from 'entities/template/template';
import {
  ITemplateValidationCounts,
  ITemplateValidationPayload,
  ITemplateValidationResult,
} from '../../entities/templates';
import { TEMPLATE_EDITOR_DEFAULT_PERCENTAGE } from './constants';
import Spinner from 'core/ui/components/spinner';
import { ILinkConfig, ILinkLookup } from '../../entities/link-config';
import { Site } from 'sections/client-management/entities';
import { TemplateApiEndpoints } from '../../constants/api-endpoints';
import { isEqual } from 'core/ui/utils/isEqual';
import { TemplatePreviewSideTabBar } from './components/TemplatePreviewSideTabBar';
import { generatePath } from 'react-router';
import { TemplateEditorDone } from './components/TemplateEditorDone';
import { useFeatureFlags } from 'core/app/AppContext';
import { TemplateManagementRoute } from '../../routes';

export const TemplateEditor: React.FC = () => {
  const state = useTemplateEditorState();
  const { pathname, search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const history = useHistory();
  const { flagEnabled } = useFeatureFlags();
  const { rightWindowPercentage, template, originalTemplate, site, previewingVersion: versionId } = state;
  const dispatch = useTemplateEditorDispatch();
  const { siteId, id: templateId, language } = useParams<{ siteId: string; id: string; language: string }>();
  const [leftWindowPercentage, setLeftWindowPercentage] = React.useState<number>(100 - rightWindowPercentage);
  const onsiteTemplate = pathname.includes('onsite');
  const siteQuery = useQuery(['site', siteId], async () => {
    const result = await RestApi.get<Site>(generatePath(TemplateApiEndpoints.GET_SITE, { siteId }));
    return result.data;
  });
  // Load template, load earlier version if versionId specified
  const templatesQuery = useQuery(['template', siteId, templateId, language, versionId], async () => {
    let endpoint;
    switch (true) {
      case pathname.includes('/email-tracking-unsubscribes/'):
        endpoint = TemplateApiEndpoints.GET_EMAIL_TRACKING_UNSUBSCRIBE_TEMPLATE;
        break;
      case pathname.includes('/unsubscribes/'):
        endpoint = TemplateApiEndpoints.GET_UNSUBSCRIBE_TEMPLATE;
        break;
      case pathname.includes('onsite'):
        endpoint =
          versionId.length > 0
            ? TemplateApiEndpoints.GET_ONSITE_TEMPLATE_VERSION
            : TemplateApiEndpoints.GET_ONSITE_TEMPLATE;
        break;
      case versionId.length > 0:
        endpoint = TemplateApiEndpoints.GET_TEMPLATE_VERSION;
        break;
      default:
        endpoint = TemplateApiEndpoints.GET_TEMPLATE;
    }
    const params =
      versionId.length > 0 ? { siteId, templateId, language, versionId } : { siteId, templateId, language };
    const result = await RestApi.get<ITemplate>(generatePath(endpoint, params));
    return result.data;
  });

  const linkLookupQuery = useQuery(['linklookup', siteId], async () => {
    const result = await RestApi.get<ILinkLookup[]>(generatePath(TemplateApiEndpoints.GET_LINK_LOOKUPS, { siteId }));
    return result.data;
  });

  const linkConfigQuery = useQuery(['linkconfig', siteId], async () => {
    const result = await RestApi.get<ILinkConfig[]>(generatePath(TemplateApiEndpoints.GET_LINK_CONFIGS, { siteId }));
    return result.data;
  });

  const isUnsubscribeLandingEditor =
    pathname.includes('/unsubscribes/') || pathname.includes('/email-tracking-unsubscribes/');

  const templateValidationQuery = useQuery(['validation', siteId, template.template], async () => {
    if (!template || template.template === '' || onsiteTemplate || isUnsubscribeLandingEditor) {
      return {
        counts: {
          fail: 0,
          pass: 0,
          warning: 0,
        },
        results: {},
      } as ITemplateValidationResult;
    }
    const payload: ITemplateValidationPayload = {
      template: {
        type: 'body',
        value: template.template,
      },
    };
    const result = await RestApi.post<ITemplateValidationResult, ITemplateValidationPayload>(
      generatePath(TemplateApiEndpoints.VALIDATE),
      payload,
    );
    return result.data;
  });

  // Redirect when viewing RCS template but ld-rcs flag is off
  React.useEffect(() => {
    if (template?.channel === 'rcs' && template?.id && !flagEnabled('ld-rcs')) {
      history.push(generatePath(TemplateManagementRoute.HOME, { siteId, channel: 'email' }));
    }
  }, [template?.channel, template?.id, flagEnabled, history, siteId]);

  // Redirect to RCS editor when ld-rcs flag is on so only the RCS editor is used for RCS templates
  React.useEffect(() => {
    if (template?.channel === 'rcs' && template?.id && flagEnabled('ld-rcs')) {
      history.replace(generatePath(TemplateManagementRoute.EDIT_RCS, { siteId, id: template.id, language }));
    }
  }, [template?.channel, template?.id, language, flagEnabled, history, siteId]);

  // Initial site load
  React.useEffect(() => {
    if (!siteQuery.isLoading) {
      const loadedSite: Site = siteQuery?.data ?? null;
      dispatch({
        type: 'SET_SITE_INITIAL',
        payload: { site: loadedSite },
      });
    }
  }, [siteQuery.isLoading, dispatch, siteQuery.data]);

  // Initial template load
  React.useEffect(() => {
    if (!templatesQuery.isLoading) {
      const loadedTemplate: ITemplate = templatesQuery?.data ?? null;
      const selectedLanguage = loadedTemplate?.supportedLanguages?.find(l => l.language === language);
      if (onsiteTemplate) {
        dispatch({
          type: 'SET_TEMPLATE_INITIAL',
          payload: {
            template: loadedTemplate,
            language: language,
            languageStatus: selectedLanguage?.status,
            onsiteValidationMessage: selectedLanguage?.validationMessage,
          },
        });
      } else {
        dispatch({
          type: 'SET_TEMPLATE_INITIAL',
          payload: {
            template: loadedTemplate,
            language: language,
            languageStatus: selectedLanguage?.status,
          },
        });
      }
      if (searchParams.get('newtab') === '1') {
        dispatch({
          type: 'SET_EDITING_IN_TAB',
          payload: {
            editingInTab: true,
          },
        });
        history.replace(history.location.pathname);
      }
    }
  }, [templatesQuery.isLoading, dispatch, templatesQuery.data, language, searchParams, history, onsiteTemplate]);

  // Initial link lookup load
  React.useEffect(() => {
    if (!linkLookupQuery.isLoading) {
      const loadedLinkLookups: ILinkLookup[] = linkLookupQuery?.data ?? [];
      dispatch({
        type: 'SET_LINK_LOOKUPS_INITIAL',
        payload: { linkLookups: loadedLinkLookups },
      });
    }
  }, [linkLookupQuery.isLoading, dispatch, linkLookupQuery.data]);

  // Initial link config load
  React.useEffect(() => {
    if (!linkConfigQuery.isLoading) {
      const loadedLinkConfig: ILinkConfig[] = linkConfigQuery?.data ?? [];
      dispatch({
        type: 'SET_LINK_CONFIGS_INITIAL',
        payload: { linkConfigs: loadedLinkConfig },
      });
    }
  }, [linkConfigQuery.isLoading, dispatch, linkConfigQuery.data]);

  // Initial validation load
  React.useEffect(() => {
    if (!templateValidationQuery.isLoading) {
      const loadedTemplateValidation: ITemplateValidationResult = templateValidationQuery?.data ?? null;
      const counts = {
        fail: loadedTemplateValidation?.counts?.fail,
        warning: loadedTemplateValidation?.counts?.warning,
        pass: loadedTemplateValidation?.counts?.pass,
      } as ITemplateValidationCounts;
      const keys = loadedTemplateValidation?.results ? Object.keys(loadedTemplateValidation.results) : [];
      const values = loadedTemplateValidation?.results ? Object.values(loadedTemplateValidation.results) : [];
      const results = [];
      values.forEach((itm, i) => {
        return results.push(Object.assign({ rule: keys[i] }, itm));
      });
      dispatch({
        type: 'SET_TEMPLATE_VALIDATION_INITIAL',
        payload: {
          counts,
          results,
        },
      });
    }
  }, [templateValidationQuery.isLoading, dispatch, templateValidationQuery.data]);

  // Set left window percentage
  React.useEffect(() => {
    setLeftWindowPercentage(100 - rightWindowPercentage);
  }, [rightWindowPercentage]);

  // Check for changes to the campaign
  React.useEffect(() => {
    if (isEqual(template.template, originalTemplate.template)) {
      dispatch({
        type: 'SET_UNSAVED_CHANGES',
        payload: { hasUnsavedChanges: false },
      });
    } else {
      dispatch({
        type: 'SET_UNSAVED_CHANGES',
        payload: { hasUnsavedChanges: true },
      });
    }
  }, [template.template, originalTemplate.template, dispatch, originalTemplate, onsiteTemplate]);

  const onDragEnd = (sizes: number[]) => {
    if (Number.isNaN(sizes[1]) || sizes[1] >= 100) {
      sizes[1] = TEMPLATE_EDITOR_DEFAULT_PERCENTAGE;
    }
    dispatch({
      type: 'SET_RIGHT_WINDOW_PERCENTAGE',
      payload: {
        percentage: sizes[1],
      },
    });
  };

  if (
    siteQuery.isLoading ||
    templatesQuery.isLoading ||
    linkLookupQuery.isLoading ||
    linkConfigQuery.isLoading ||
    !site ||
    !template ||
    template.id === ''
  ) {
    return <Spinner />;
  }

  return (
    <Suspense fallback={<AppLoader />}>
      <Overlay>
        <Layout data-testid="TemplateEditorLayout">
          {(!state.editingInTab || !state.editingDone) && (
            <>
              <TabBarAndWindowLayout data-testid="TemplateEditorMainSection">
                <TemplateEditorNameBar />
                <SplitPanelWrapper>
                  <TemplateEditorSideTabBar data-testid="TemplateEditorTabs" />
                  <SplitPanelContainer
                    id={TEMPLATE_EDITOR_SPLIT_CONTAINER_CSS_ID}
                    sizes={[leftWindowPercentage, rightWindowPercentage]}
                    gutterSize={5}
                    direction="horizontal"
                    onDragEnd={onDragEnd}
                  >
                    <div>
                      <TemplateEditorLeftWindow />
                    </div>
                    <div>
                      <SplitPanelWrapper>
                        <div>
                          <TemplatePreviewSideTabBar data-testid="TemplateEditorTabs" />
                        </div>
                        <TemplateEditorRightWindow />
                      </SplitPanelWrapper>
                    </div>
                  </SplitPanelContainer>
                </SplitPanelWrapper>
              </TabBarAndWindowLayout>
              <TemplateEditorBottomToolbar />
            </>
          )}
          {state.editingInTab && state.editingDone && (
            <TabBarAndWindowLayout data-testid="TemplateEditorMainSection">
              <TemplateEditorDone />
            </TabBarAndWindowLayout>
          )}
        </Layout>
      </Overlay>
    </Suspense>
  );
};

export default TemplateEditor;
